'use client';

import { useEffect, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { isDarkTheme } from '@/lib/helpers';
import { DashWidget, Plant } from '@/lib/types';
import ApexChartWrapper from '../ApexChartWrapper';

interface Props { widget: DashWidget; plant: Plant; }

/*
 * Resort Operations — the operational side of a resort that energy meters alone
 * don't cover, in four tabs:
 *   • Guests       — occupancy, guests in-house, arrivals/departures, room blocks
 *   • Cost / unit  — electricity + water + LPG cost per room-night, per F&B cover
 *                    and per kg of laundry, with kWh/ORN and L/guest-night
 *   • Utilities    — power, water supply & tanks, LPG, generator, pool
 *   • Wastewater   — WWTP process train, effluent vs the building-discharge
 *                    standard, outdoor environment
 *
 * DEMO DATA. Every figure is generated in-widget and badged as such. History is
 * seeded by calendar day, so a page reload shows the same month rather than a
 * new random one; the "now" values drift on a slow tick. The site-specific
 * inputs (room blocks, tariffs, allocation keys) come from widget.resortOps so a
 * later real feed — PMS for guests, water/LPG meters, WWTP sensors — replaces
 * the generators without touching the layout.
 */

type Tab = 'guests' | 'cost' | 'utility' | 'wwtp';
const LABEL: Record<Tab, [string, string]> = {
  guests: ['ผู้เข้าพัก', 'Guests'], cost: ['ต้นทุนต่อหน่วย', 'Cost per unit'], utility: ['สาธารณูปโภค', 'Utilities'], wwtp: ['น้ำเสีย & สิ่งแวดล้อม', 'Wastewater & environment'],
};

const S = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#7b5fd6', '#0e97ad'];
const OK = '#1baf7a', WARN = '#eda100', BAD = '#e34948';
const TXT = 'var(--text-primary)';
const MUT = 'color-mix(in srgb, var(--text-muted) 60%, var(--text-primary))';
const BD = 'color-mix(in srgb, var(--text-muted) 34%, transparent)';
const tnum = { fontVariantNumeric: 'tabular-nums' as const };

const DAY_MS = 86_400_000;
const BKK_MS = 7 * 3_600_000;
const HIST = 30;

const DEFAULT_BLOCKS = [
  { label: 'GR 7–32', rooms: 26 },
  { label: 'GR 39–62', rooms: 24 },
  { label: 'GR 63–77', rooms: 15 },
  { label: 'VIP 78–81', rooms: 4 },
];

// Allocation keys: what share of each utility each revenue department draws.
// Common areas (lobby, BOH, pool, landscape) are already folded in.
const ALLOC = {
  elec: { rooms: 0.70, fb: 0.21, laundry: 0.09 },
  water: { rooms: 0.64, fb: 0.16, laundry: 0.20 },
  lpg: { rooms: 0, fb: 0.72, laundry: 0.28 },
};

/*
 * Effluent limits — Thai MoNRE notification (2548/2005) on discharge from
 * certain building types. A hotel of 60–199 rooms is Type B (ประเภท ข).
 * Confirm the class and limits against the site's permit before relying on it.
 */
const EFFLUENT = [
  { key: 'bod', th: 'BOD', en: 'BOD', unit: 'mg/L', limit: 30, base: 12, amp: 5, src: 'lab' },
  { key: 'ss', th: 'สารแขวนลอย (SS)', en: 'Suspended solids', unit: 'mg/L', limit: 40, base: 17, amp: 7, src: 'online' },
  { key: 'tkn', th: 'TKN', en: 'TKN', unit: 'mg/L', limit: 35, base: 14, amp: 5, src: 'lab' },
  { key: 'fog', th: 'น้ำมันและไขมัน', en: 'Fat, oil & grease', unit: 'mg/L', limit: 20, base: 6, amp: 3, src: 'lab' },
  { key: 'sulfide', th: 'ซัลไฟด์', en: 'Sulfide', unit: 'mg/L', limit: 1.0, base: 0.35, amp: 0.2, src: 'lab' },
  { key: 'settle', th: 'ตะกอนหนัก', en: 'Settleable solids', unit: 'mL/L', limit: 0.5, base: 0.12, amp: 0.08, src: 'lab' },
];

function seeded(n: number): number {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface DayRow {
  day: number; label: string; month: number; dow: number;
  roomsOcc: number; occ: number; guests: number; arrivals: number; departures: number;
  covers: number; laundryKg: number; kwh: number; waterM3: number; lpgKg: number;
  bod: number; ss: number; wwIn: number;
}

function buildDay(day: number, rooms: number): DayRow {
  const d = new Date(day * DAY_MS);
  const dow = d.getUTCDay();
  const r = (k: number) => seeded(day * 31 + k);
  // Fri/Sat nights fill up; a slow wave stands in for the booking cycle.
  const weekend = dow === 5 || dow === 6 ? 0.13 : dow === 0 ? 0.05 : 0;
  const occ = clamp(0.6 + weekend + 0.07 * Math.sin(day / 4.3) + (r(1) - 0.5) * 0.12, 0.32, 0.97);
  const roomsOcc = Math.round(occ * rooms);
  const guests = Math.round(roomsOcc * (1.7 + r(2) * 0.35));
  const arrivals = Math.round((roomsOcc / 2.8) * (0.75 + r(3) * 0.5));
  const departures = Math.round((roomsOcc / 2.8) * (0.75 + r(4) * 0.5));
  const covers = Math.round(guests * 1.8 + 25 + r(5) * 45);
  const laundryKg = Math.round(roomsOcc * 3.2 + covers * 0.15);
  const kwh = Math.round(1150 + roomsOcc * 33 + covers * 1.1 + (r(6) - 0.5) * 220);
  const waterM3 = +(16 + guests * 0.43 + covers * 0.02 + (r(7) - 0.5) * 6).toFixed(1);
  const lpgKg = +(covers * 0.085 + laundryKg * 0.05 + (r(8) - 0.5) * 3).toFixed(1);
  // One day in the month runs close to the BOD limit, so the chart shows what
  // "near the line" looks like instead of a flat, reassuring trace.
  const spike = r(9) > 0.94 ? 12 : 0;
  return {
    day, dow, month: d.getUTCMonth(),
    label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
    roomsOcc, occ, guests, arrivals, departures, covers, laundryKg, kwh, waterM3, lpgKg,
    bod: +(12 + (r(10) - 0.5) * 9 + spike).toFixed(1),
    ss: +(17 + (r(11) - 0.5) * 13 + spike * 0.8).toFixed(1),
    wwIn: +(waterM3 * (0.76 + r(12) * 0.08)).toFixed(1),
  };
}

export default function ResortOpsWidget({ widget }: Props) {
  const th = useAppStore((s) => s.lang) === 'th';
  const dark = isDarkTheme();
  const ALL: Tab[] = ['guests', 'cost', 'utility', 'wwtp'];
  const shown = (widget.resortOps?.sections || ALL).filter((x) => ALL.includes(x));
  const [tab, setTab] = useState<Tab>(shown[0] ?? 'guests');
  const [tick, setTick] = useState(0);
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => { setTick((t) => t + 1); setClock(Date.now()); }, 5000);
    return () => clearInterval(id);
  }, []);

  const cfg = widget.resortOps || {};
  const blocks = cfg.roomBlocks?.length ? cfg.roomBlocks : DEFAULT_BLOCKS;
  const ROOMS = blocks.reduce((a, b) => a + b.rooms, 0);
  const rate = { elec: cfg.elecRate ?? 4.0, water: cfg.waterRate ?? 31, lpg: cfg.lpgRate ?? 28 };

  const bkk = clock + BKK_MS;
  const today = Math.floor(bkk / DAY_MS);
  const hourNow = (bkk % DAY_MS) / 3_600_000;
  const dayFrac = Math.max(0.04, hourNow / 24);

  // Thirty seeded days are cheap to rebuild on the 5 s tick; no memo needed.
  const hist = Array.from({ length: HIST }, (_, i) => buildDay(today - (HIST - 1 - i), ROOMS));
  const todayRow = hist[hist.length - 1];
  const thisMonth = todayRow.month;
  // Month to date: finished days in full, today pro-rata to the hour.
  const mtdRows = hist.filter((d) => d.month === thisMonth);
  const mtd = (k: keyof DayRow) =>
    mtdRows.reduce((a, d) => a + (d.day === today ? (d[k] as number) * dayFrac : (d[k] as number)), 0);

  const wave = (base: number, amp: number, phase: number, period = 7) =>
    base + Math.sin((tick + phase) / period) * amp + (seeded(tick * 7 + phase) - 0.5) * amp * 0.35;

  const int = (v: number) => Math.round(v).toLocaleString();
  const f1 = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const f2 = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const baht = (v: number) => `฿${int(v)}`;
  const pct = (share: number) => Math.round(share * 100);

  const axisCol = dark ? '#94a3b8' : '#64748b';
  const gridCol = dark ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.14)';
  const baseChart = (type: 'bar' | 'line' | 'area'): ApexCharts.ApexOptions => ({
    chart: { type, toolbar: { show: false }, animations: { enabled: false }, background: 'transparent', fontFamily: 'inherit' },
    dataLabels: { enabled: false },
    grid: { borderColor: gridCol, strokeDashArray: 3, xaxis: { lines: { show: false } }, padding: { left: 4, right: 8, top: 0, bottom: 0 } },
    legend: { show: true, position: 'top', horizontalAlign: 'left', fontSize: '11px', labels: { colors: axisCol }, markers: { size: 5 } },
    tooltip: { theme: dark ? 'dark' : 'light', shared: true, intersect: false },
    xaxis: { labels: { style: { colors: axisCol, fontSize: '10px' }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
  });

  // ── shared pieces ──────────────────────────────────────────────────────────
  const card = (accent?: string): React.CSSProperties => ({
    background: accent ? `linear-gradient(180deg, color-mix(in srgb, ${accent} 5%, var(--bg-surface)), var(--bg-surface) 70%)` : 'var(--bg-surface)',
    border: `1px solid ${accent ? `color-mix(in srgb, ${accent} 24%, var(--glass-border))` : 'var(--glass-border)'}`,
    borderRadius: 14, padding: '10px 13px',
    boxShadow: '0 1px 2px rgba(16,24,40,.045), 0 10px 22px -18px rgba(16,24,40,.4)',
  });
  const Kpi = (o: { label: string; value: string; unit?: string; sub?: string; accent: string; state?: 'ok' | 'warn' | 'bad' }) => (
    <div style={card(o.accent)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: o.state ? { ok: OK, warn: WARN, bad: BAD }[o.state] : o.accent, flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: MUT, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 5 }}>
        <span style={{ fontSize: 21, fontWeight: 800, color: TXT, letterSpacing: '-0.02em', ...tnum }}>{o.value}</span>
        {o.unit && <span style={{ fontSize: 10.5, color: MUT, fontWeight: 600 }}>{o.unit}</span>}
      </div>
      {o.sub && <div style={{ fontSize: 10.5, color: MUT, marginTop: 3, ...tnum }}>{o.sub}</div>}
    </div>
  );
  const kpiGrid = (children: React.ReactNode) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>{children}</div>
  );
  const Panel = (o: { title: string; note?: string; children: React.ReactNode; grow?: string; minW?: number }) => (
    <div style={{ ...card(), flex: o.grow || '1 1 320px', minWidth: o.minW ?? 260, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: MUT, textTransform: 'uppercase', letterSpacing: '.06em' }}>{o.title}</span>
        {o.note && <span style={{ fontSize: 10, color: MUT, opacity: 0.8, marginLeft: 'auto' }}>{o.note}</span>}
      </div>
      {o.children}
    </div>
  );
  const row = (children: React.ReactNode) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>{children}</div>
  );
  const bar = (pct: number, color: string) => (
    <div style={{ height: 6, borderRadius: 99, background: `color-mix(in srgb, ${color} 14%, transparent)`, overflow: 'hidden' }}>
      <div style={{ width: `${clamp(pct, 0, 100)}%`, height: '100%', background: color, borderRadius: 99 }} />
    </div>
  );
  const chip = (state: 'ok' | 'warn' | 'bad' | 'idle', text: string) => {
    const c = { ok: OK, warn: WARN, bad: BAD, idle: 'var(--text-muted)' }[state];
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, color: c, padding: '2px 8px', borderRadius: 99, background: `color-mix(in srgb, ${c} 12%, transparent)`, whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: 99, background: c }} />{text}
      </span>
    );
  };
  const line = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12, padding: '3px 0', borderTop: `1px solid ${BD}` }}>
      <span style={{ color: MUT, flex: 1 }}>{label}</span>
      <span style={{ color: TXT, fontWeight: 700, ...tnum }}>{value}</span>
    </div>
  );

  // ── Guests ─────────────────────────────────────────────────────────────────
  const guestsTab = () => {
    const inHouse = Math.round(todayRow.guests * (hourNow < 14 ? 0.97 : 1));
    const occPct = todayRow.occ * 100;
    const lastWeek = hist[hist.length - 8];
    const guestNightsMtd = mtd('guests');
    const roomNightsMtd = mtd('roomsOcc');
    const avgOccMtd = (roomNightsMtd / (ROOMS * (mtdRows.length - 1 + dayFrac))) * 100;
    const los = 2.6 + seeded(today) * 0.6;

    // Spread today's occupied rooms over the blocks; VIP villas fill a little lighter.
    let left = todayRow.roomsOcc;
    const blockRows = blocks.map((b, i) => {
      const bias = /vip/i.test(b.label) ? 0.85 : 1;
      const want = i === blocks.length - 1 ? left : Math.round(b.rooms * todayRow.occ * bias * (0.92 + seeded(today + i) * 0.16));
      const occ = clamp(want, 0, Math.min(b.rooms, left));
      left -= occ;
      return { ...b, occ, color: S[i % S.length] };
    });

    const opts: ApexCharts.ApexOptions = {
      ...baseChart('line'),
      colors: [S[0], S[1]],
      stroke: { width: [0, 2.5], curve: 'smooth' },
      plotOptions: { bar: { columnWidth: '62%', borderRadius: 3, borderRadiusApplication: 'end' } },
      xaxis: { ...baseChart('line').xaxis, categories: hist.map((d) => d.label), tickAmount: 8 },
      yaxis: [
        { min: 0, max: 100, tickAmount: 4, labels: { style: { colors: axisCol, fontSize: '10px' }, formatter: (v: number) => `${Math.round(v)}%` } },
        { opposite: true, min: 0, tickAmount: 4, labels: { style: { colors: axisCol, fontSize: '10px' }, formatter: (v: number) => int(v) } },
      ],
      tooltip: { ...baseChart('line').tooltip, y: { formatter: (v: number, o?: { seriesIndex: number }) => (o?.seriesIndex === 0 ? `${f1(v)}%` : `${int(v)} ${th ? 'คน' : 'guests'}`) } },
    };
    const series = [
      { name: th ? 'อัตราเข้าพัก' : 'Occupancy', type: 'column', data: hist.map((d) => +(d.occ * 100).toFixed(1)) },
      { name: th ? 'แขกเข้าพัก' : 'Guests', type: 'line', data: hist.map((d) => d.guests) },
    ];

    return (
      <>
        {kpiGrid(<>
          {Kpi({ label: th ? 'อัตราเข้าพักวันนี้' : 'Occupancy today', value: f1(occPct), unit: '%', accent: S[0], sub: `${th ? 'สัปดาห์ก่อน' : 'Last week'} ${f1(lastWeek.occ * 100)}%` })}
          {Kpi({ label: th ? 'ห้องที่มีแขก' : 'Rooms occupied', value: `${todayRow.roomsOcc}`, unit: `/ ${ROOMS}`, accent: S[2], sub: `${ROOMS - todayRow.roomsOcc} ${th ? 'ห้องว่าง' : 'vacant'}` })}
          {Kpi({ label: th ? 'แขกในโรงแรมตอนนี้' : 'Guests in-house', value: int(inHouse), unit: th ? 'คน' : 'pax', accent: S[1], sub: `${f2(todayRow.guests / Math.max(1, todayRow.roomsOcc))} ${th ? 'คน/ห้อง' : 'per room'}` })}
          {Kpi({ label: th ? 'เช็คอินวันนี้' : 'Arrivals today', value: `${todayRow.arrivals}`, unit: th ? 'ห้อง' : 'rooms', accent: S[3], sub: `${Math.min(todayRow.arrivals, Math.round(todayRow.arrivals * Math.min(1, Math.max(0, (hourNow - 12) / 8))))} ${th ? 'เข้าแล้ว' : 'checked in'}` })}
          {Kpi({ label: th ? 'เช็คเอาท์วันนี้' : 'Departures today', value: `${todayRow.departures}`, unit: th ? 'ห้อง' : 'rooms', accent: S[4], sub: `${Math.min(todayRow.departures, Math.round(todayRow.departures * Math.min(1, Math.max(0, (hourNow - 7) / 5))))} ${th ? 'ออกแล้ว' : 'checked out'}` })}
          {Kpi({ label: th ? 'Guest-nights เดือนนี้' : 'Guest-nights MTD', value: int(guestNightsMtd), accent: S[5], sub: `${th ? 'เฉลี่ยเข้าพัก' : 'Avg occupancy'} ${f1(avgOccMtd)}% · LOS ${f1(los)} ${th ? 'คืน' : 'nights'}` })}
        </>)}
        {row(<>
          {Panel({
            title: th ? 'อัตราเข้าพัก & จำนวนแขก' : 'Occupancy & guests', note: th ? '30 วันล่าสุด' : 'last 30 days', grow: '2 1 480px',
            children: <div style={{ height: 250 }}><ApexChartWrapper options={opts} series={series} type="line" height="100%" /></div>,
          })}
          {Panel({
            title: th ? 'ห้องพักตามอาคาร · วันนี้' : 'Room blocks · today', minW: 240,
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 2 }}>
                {blockRows.map((b) => (
                  <div key={b.label}>
                    <div style={{ display: 'flex', fontSize: 12, marginBottom: 4, ...tnum }}>
                      <span style={{ color: TXT, fontWeight: 700, flex: 1 }}>{b.label}</span>
                      <span style={{ color: MUT }}>{b.occ}/{b.rooms}</span>
                      <span style={{ color: TXT, fontWeight: 700, width: 46, textAlign: 'right' }}>{Math.round((b.occ / b.rooms) * 100)}%</span>
                    </div>
                    {bar((b.occ / b.rooms) * 100, b.color)}
                  </div>
                ))}
                <div style={{ marginTop: 4 }}>
                  {line(th ? 'Room-nights เดือนนี้' : 'Room-nights MTD', int(roomNightsMtd))}
                  {line(th ? 'ห้องรอทำความสะอาด' : 'Rooms to clean', `${Math.max(0, Math.round(todayRow.departures * clamp((hourNow - 7) / 5, 0, 1)) - Math.round(todayRow.departures * clamp((hourNow - 10) / 6, 0, 1)))}`)}
                  {line(th ? 'แหล่งข้อมูล' : 'Source', th ? 'PMS / คีย์การ์ด (demo)' : 'PMS / keycard (demo)')}
                </div>
              </div>
            ),
          })}
        </>)}
      </>
    );
  };

  // ── Cost per unit ──────────────────────────────────────────────────────────
  const costTab = () => {
    const kwh = mtd('kwh'), water = mtd('waterM3'), lpg = mtd('lpgKg');
    const orn = mtd('roomsOcc'), gn = mtd('guests'), covers = mtd('covers'), kg = mtd('laundryKg');
    const cost = { elec: kwh * rate.elec, water: water * rate.water, lpg: lpg * rate.lpg };
    const total = cost.elec + cost.water + cost.lpg;

    const dept = [
      { key: 'rooms' as const, th: 'ห้องพัก', en: 'Rooms', unitTh: 'room-night', unitEn: 'room-night', units: orn, color: S[0] },
      { key: 'fb' as const, th: 'อาหาร & เครื่องดื่ม', en: 'F&B', unitTh: 'cover', unitEn: 'cover', units: covers, color: S[1] },
      { key: 'laundry' as const, th: 'ซักรีด', en: 'Laundry', unitTh: 'kg ผ้า', unitEn: 'kg linen', units: kg, color: S[2] },
    ].map((d) => {
      const e = cost.elec * ALLOC.elec[d.key], w = cost.water * ALLOC.water[d.key], l = cost.lpg * ALLOC.lpg[d.key];
      return {
        ...d, e, w, l, sum: e + w + l, per: (e + w + l) / Math.max(1, d.units),
        kwhPer: (kwh * ALLOC.elec[d.key]) / Math.max(1, d.units),
        lPer: (water * ALLOC.water[d.key] * 1000) / Math.max(1, d.units),
      };
    });

    const kwhPerOrn = kwh / Math.max(1, orn);
    const lPerGn = (water * 1000) / Math.max(1, gn);
    // Rough resort benchmarks; a site sets its own targets once it has a baseline.
    const KWH_TARGET = 75, L_TARGET = 600;

    const daily = hist.slice(-14);
    const opts: ApexCharts.ApexOptions = {
      ...baseChart('bar'),
      chart: { ...baseChart('bar').chart, stacked: true },
      colors: [S[3], S[0], S[1]],
      plotOptions: { bar: { columnWidth: '58%', borderRadius: 3, borderRadiusApplication: 'end' } },
      xaxis: { ...baseChart('bar').xaxis, categories: daily.map((d) => d.label) },
      yaxis: { tickAmount: 4, labels: { style: { colors: axisCol, fontSize: '10px' }, formatter: (v: number) => `฿${Math.round(v)}` } },
      tooltip: { ...baseChart('bar').tooltip, y: { formatter: (v: number) => `฿${f1(v)}` } },
    };
    const perOrn = (d: DayRow, k: 'kwh' | 'waterM3' | 'lpgKg', r: number, share: number) => +((d[k] * r * share) / Math.max(1, d.roomsOcc)).toFixed(1);
    const series = [
      { name: th ? 'ค่าไฟ' : 'Electricity', data: daily.map((d) => perOrn(d, 'kwh', rate.elec, ALLOC.elec.rooms)) },
      { name: th ? 'ค่าน้ำ' : 'Water', data: daily.map((d) => perOrn(d, 'waterM3', rate.water, ALLOC.water.rooms)) },
      { name: 'LPG', data: daily.map((d) => perOrn(d, 'lpgKg', rate.lpg, ALLOC.lpg.rooms)) },
    ];

    const th_ = { padding: '7px 8px', fontSize: 10, fontWeight: 800, color: MUT, textTransform: 'uppercase' as const, letterSpacing: '.04em', textAlign: 'right' as const, whiteSpace: 'nowrap' as const };
    const td = { padding: '8px 8px', fontSize: 12.5, color: TXT, textAlign: 'right' as const, borderTop: `1px solid ${BD}`, whiteSpace: 'nowrap' as const, ...tnum };

    return (
      <>
        {kpiGrid(<>
          {Kpi({ label: th ? 'ต้นทุนสาธารณูปโภค/ห้อง-คืน' : 'Utility cost / room-night', value: baht(dept[0].per), accent: S[0], sub: th ? 'ค่าไฟ+น้ำ ส่วนของห้องพัก' : 'rooms share of power + water' })}
          {Kpi({ label: th ? 'ต้นทุนรวม/แขก-คืน' : 'Total cost / guest-night', value: baht(total / Math.max(1, gn)), accent: S[1], sub: th ? 'ทุกแผนก รวม LPG' : 'all departments incl. LPG' })}
          {Kpi({ label: 'kWh / room-night', value: f1(kwhPerOrn), accent: S[3], state: kwhPerOrn <= KWH_TARGET ? 'ok' : 'warn', sub: `${th ? 'ทั้งรีสอร์ต · เป้า' : 'resort-wide · target'} ≤ ${KWH_TARGET}` })}
          {Kpi({ label: th ? 'ลิตร / แขก-คืน' : 'Litres / guest-night', value: int(lPerGn), unit: 'L', accent: S[6], state: lPerGn <= L_TARGET ? 'ok' : 'warn', sub: `${th ? 'ทั้งรีสอร์ต · เป้า' : 'resort-wide · target'} ≤ ${L_TARGET} L` })}
          {Kpi({ label: th ? 'ค่าสาธารณูปโภคเดือนนี้' : 'Utility bill MTD', value: baht(total), accent: S[4], sub: `${th ? 'ไฟ' : 'Power'} ${Math.round((cost.elec / total) * 100)}% · ${th ? 'น้ำ' : 'water'} ${Math.round((cost.water / total) * 100)}% · LPG ${Math.round((cost.lpg / total) * 100)}%` })}
        </>)}
        {row(<>
          {Panel({
            title: th ? 'ต้นทุนต่อหน่วยสินค้า · เดือนนี้' : 'Cost per product unit · MTD', grow: '3 1 520px',
            children: (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th_, textAlign: 'left' }}>{th ? 'แผนก / หน่วยสินค้า' : 'Department / unit'}</th>
                      <th style={th_}>{th ? 'ยอดขาย' : 'Units'}</th>
                      <th style={th_}>{th ? 'ค่าไฟ' : 'Power'}</th>
                      <th style={th_}>{th ? 'ค่าน้ำ' : 'Water'}</th>
                      <th style={th_}>LPG</th>
                      <th style={th_}>{th ? 'รวม' : 'Total'}</th>
                      <th style={th_}>kWh/{th ? 'หน่วย' : 'unit'}</th>
                      <th style={th_}>L/{th ? 'หน่วย' : 'unit'}</th>
                      <th style={{ ...th_, color: TXT }}>฿/{th ? 'หน่วย' : 'unit'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dept.map((d) => (
                      <tr key={d.key}>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: d.color, marginRight: 7 }} />
                          <b>{th ? d.th : d.en}</b> <span style={{ color: MUT }}>· {th ? d.unitTh : d.unitEn}</span>
                        </td>
                        <td style={td}>{int(d.units)}</td>
                        <td style={td}>{baht(d.e)}</td>
                        <td style={td}>{baht(d.w)}</td>
                        <td style={td}>{d.l ? baht(d.l) : '—'}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{baht(d.sum)}</td>
                        <td style={td}>{f2(d.kwhPer)}</td>
                        <td style={td}>{int(d.lPer)}</td>
                        <td style={{ ...td, fontWeight: 800, fontSize: 14, color: d.color }}>฿{f2(d.per)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 800 }}>{th ? 'รวมทั้งรีสอร์ต' : 'Resort total'}</td>
                      <td style={td} />
                      <td style={{ ...td, fontWeight: 700 }}>{baht(cost.elec)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{baht(cost.water)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{baht(cost.lpg)}</td>
                      <td style={{ ...td, fontWeight: 800 }}>{baht(total)}</td>
                      <td style={td} /><td style={td} /><td style={td} />
                    </tr>
                  </tbody>
                </table>
                <div style={{ fontSize: 10.5, color: MUT, marginTop: 8, lineHeight: 1.5 }}>
                  {th
                    ? `ใช้ไป ${int(kwh)} kWh × ฿${f2(rate.elec)} · น้ำ ${f1(water)} m³ × ฿${f2(rate.water)} · LPG ${f1(lpg)} kg × ฿${f2(rate.lpg)} · สัดส่วนปันส่วน ไฟ ห้องพัก/F&B/ซักรีด = ${pct(ALLOC.elec.rooms)}/${pct(ALLOC.elec.fb)}/${pct(ALLOC.elec.laundry)}% · น้ำ ${pct(ALLOC.water.rooms)}/${pct(ALLOC.water.fb)}/${pct(ALLOC.water.laundry)}% · LPG F&B/ซักรีด ${pct(ALLOC.lpg.fb)}/${pct(ALLOC.lpg.laundry)}% (รวมพื้นที่ส่วนกลางแล้ว)`
                    : `${int(kwh)} kWh × ฿${f2(rate.elec)} · water ${f1(water)} m³ × ฿${f2(rate.water)} · LPG ${f1(lpg)} kg × ฿${f2(rate.lpg)} · allocation power rooms/F&B/laundry = ${pct(ALLOC.elec.rooms)}/${pct(ALLOC.elec.fb)}/${pct(ALLOC.elec.laundry)}% · water ${pct(ALLOC.water.rooms)}/${pct(ALLOC.water.fb)}/${pct(ALLOC.water.laundry)}% · LPG F&B/laundry ${pct(ALLOC.lpg.fb)}/${pct(ALLOC.lpg.laundry)}% (common areas folded in)`}
                </div>
              </div>
            ),
          })}
          {Panel({
            title: th ? 'ต้นทุนห้องพักต่อห้อง-คืน' : 'Rooms cost per room-night', note: th ? '14 วัน · ฿' : '14 days · ฿', grow: '2 1 360px',
            children: <div style={{ height: 250 }}><ApexChartWrapper options={opts} series={series} type="bar" height="100%" /></div>,
          })}
        </>)}
      </>
    );
  };

  // ── Utilities ──────────────────────────────────────────────────────────────
  const utilityTab = () => {
    // Hour-of-day shapes: water peaks with morning showers and the evening,
    // power with the evening dinner/air-con load.
    const hours = Array.from({ length: 24 }, (_, h) => h);
    const kwShape = (h: number) => 80 + 55 * Math.exp(-((h - 20) ** 2) / 10) + 35 * Math.exp(-((h - 13) ** 2) / 14) + 10 * Math.exp(-((h - 8) ** 2) / 4);
    const wShape = (h: number) => 0.9 + 3.6 * Math.exp(-((h - 8) ** 2) / 3.5) + 2.6 * Math.exp(-((h - 19) ** 2) / 5) + 0.9 * Math.exp(-((h - 14) ** 2) / 8);
    const scaleKw = todayRow.kwh / hours.reduce((a, h) => a + kwShape(h), 0);
    const scaleW = todayRow.waterM3 / hours.reduce((a, h) => a + wShape(h), 0);
    const hNow = Math.floor(hourNow);
    const kwSeries = hours.map((h) => (h <= hNow ? +(kwShape(h) * scaleKw * (0.94 + seeded(today * 24 + h) * 0.12)).toFixed(1) : null));
    const wSeries = hours.map((h) => (h <= hNow ? +(wShape(h) * scaleW * (0.9 + seeded(today * 48 + h) * 0.2)).toFixed(2) : null));

    const kwNow = wave(kwShape(hourNow) * scaleKw, 6, 1);
    const kwhToday = todayRow.kwh * dayFrac;
    const peakKw = Math.max(...kwSeries.filter((v): v is number => v != null), kwNow);
    const pf = wave(0.93, 0.015, 3);
    const flowNow = Math.max(0.2, wave(wShape(hourNow) * scaleW, 0.3, 2));
    const rawTank = clamp(wave(74, 4, 5, 11), 0, 100);
    const roofTank = clamp(wave(86, 5, 2, 9), 0, 100);
    const pressure = wave(3.3, 0.15, 4);
    const lpgPct = clamp(62 - hourNow * 0.35, 5, 100);
    const lpgDaysLeft = (lpgPct / 100) * 2000 / Math.max(1, todayRow.lpgKg);
    const diesel = 78;
    const poolPh = wave(7.4, 0.12, 6), poolCl = wave(1.6, 0.25, 3), poolT = wave(29.1, 0.3, 1);

    const st = (v: number, lo: number, hi: number, warnLo = lo, warnHi = hi): 'ok' | 'warn' | 'bad' =>
      v < lo || v > hi ? 'bad' : v < warnLo || v > warnHi ? 'warn' : 'ok';

    const opts: ApexCharts.ApexOptions = {
      ...baseChart('line'),
      colors: [S[3], S[6]],
      stroke: { width: [2.5, 0], curve: 'smooth' },
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 2 } },
      xaxis: { ...baseChart('line').xaxis, categories: hours.map((h) => `${String(h).padStart(2, '0')}:00`), tickAmount: 8 },
      yaxis: [
        { min: 0, tickAmount: 4, labels: { style: { colors: axisCol, fontSize: '10px' }, formatter: (v: number) => `${Math.round(v)} kW` } },
        { opposite: true, min: 0, tickAmount: 4, labels: { style: { colors: axisCol, fontSize: '10px' }, formatter: (v: number) => `${f1(v)} m³` } },
      ],
      tooltip: { ...baseChart('line').tooltip, y: { formatter: (v: number, o?: { seriesIndex: number }) => (v == null ? '—' : o?.seriesIndex === 0 ? `${f1(v)} kW` : `${f2(v)} m³/h`) } },
    };
    const series = [
      { name: th ? 'กำลังไฟฟ้า (kW)' : 'Power (kW)', type: 'line', data: kwSeries },
      { name: th ? 'น้ำประปา (m³/h)' : 'Water (m³/h)', type: 'column', data: wSeries },
    ];

    const tank = (label: string, pct: number, color: string, sub: string) => (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, minWidth: 64, flex: 1 }}>
        <div style={{ width: 38, height: 92, borderRadius: 9, border: `2px solid color-mix(in srgb, ${color} 50%, transparent)`, position: 'relative', overflow: 'hidden', background: `color-mix(in srgb, ${color} 6%, transparent)` }}>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${pct}%`, background: `color-mix(in srgb, ${color} 55%, transparent)`, transition: 'height .6s' }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 800, color: TXT, ...tnum }}>{Math.round(pct)}%</span>
        <span style={{ fontSize: 10.5, color: MUT, textAlign: 'center', lineHeight: 1.25 }}>{label}<br />{sub}</span>
      </div>
    );

    const sysCard = (o: { icon: string; title: string; state: 'ok' | 'warn' | 'bad' | 'idle'; stateText: string; lines: [string, string][] }) => (
      <div style={{ ...card(), display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
          <span style={{ fontSize: 16 }}>{o.icon}</span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: TXT, flex: 1 }}>{o.title}</span>
          {chip(o.state, o.stateText)}
        </div>
        {o.lines.map(([k, v]) => <div key={k}>{line(k, v)}</div>)}
      </div>
    );

    const ok = th ? 'ปกติ' : 'Normal';
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 8 }}>
          {sysCard({ icon: '⚡', title: th ? 'ไฟฟ้า' : 'Electricity', state: pf < 0.85 ? 'warn' : 'ok', stateText: ok, lines: [
            [th ? 'กำลังไฟตอนนี้' : 'Load now', `${f1(kwNow)} kW`],
            [th ? 'พลังงานวันนี้' : 'Energy today', `${int(kwhToday)} kWh`],
            [th ? 'พีควันนี้' : 'Peak today', `${f1(peakKw)} kW`],
            ['Power factor', f2(pf)],
          ] })}
          {sysCard({ icon: '🚰', title: th ? 'น้ำประปา' : 'Water supply', state: pressure < 2.5 ? 'warn' : 'ok', stateText: ok, lines: [
            [th ? 'อัตราไหล' : 'Flow', `${f2(flowNow)} m³/h`],
            [th ? 'ใช้วันนี้' : 'Used today', `${f1(todayRow.waterM3 * dayFrac)} m³`],
            [th ? 'แรงดันปั๊มเพิ่มแรงดัน' : 'Booster pressure', `${f2(pressure)} bar`],
            [th ? 'ปั๊มทำงาน' : 'Pumps running', hourNow > 6 && hourNow < 23 ? 'P1 · P2 standby' : 'P2 · P1 standby'],
          ] })}
          {sysCard({ icon: '🔥', title: 'LPG', state: lpgPct < 20 ? 'bad' : lpgPct < 35 ? 'warn' : 'ok', stateText: lpgPct < 35 ? (th ? 'ใกล้หมด' : 'Low') : ok, lines: [
            [th ? 'ระดับถัง' : 'Tank level', `${Math.round(lpgPct)}%`],
            [th ? 'ใช้วันนี้' : 'Used today', `${f1(todayRow.lpgKg * dayFrac)} kg`],
            [th ? 'ใช้ได้อีก' : 'Days left', `${f1(lpgDaysLeft)} ${th ? 'วัน' : 'days'}`],
            [th ? 'ผู้ใช้หลัก' : 'Main users', th ? 'ครัว · เครื่องอบผ้า' : 'Kitchen · dryers'],
          ] })}
          {sysCard({ icon: '🛢️', title: th ? 'เครื่องปั่นไฟ' : 'Generator', state: 'idle', stateText: th ? 'สแตนด์บาย' : 'Standby', lines: [
            [th ? 'น้ำมันดีเซล' : 'Diesel', `${diesel}%`],
            [th ? 'แบตเตอรี่สตาร์ท' : 'Start battery', `${f1(wave(26.8, 0.1, 2))} V`],
            [th ? 'ทดสอบล่าสุด' : 'Last test run', th ? '7 วันก่อน · 15 นาที' : '7 days ago · 15 min'],
            [th ? 'ATS' : 'ATS', th ? 'ไฟหลัก (MEA/PEA)' : 'On utility'],
          ] })}
          {sysCard({ icon: '🏊', title: th ? 'สระว่ายน้ำ' : 'Pool', state: st(poolCl, 0.6, 3, 1, 2.5) === 'ok' && st(poolPh, 7.0, 7.8, 7.2, 7.6) === 'ok' ? 'ok' : 'warn', stateText: ok, lines: [
            ['pH', f2(poolPh)],
            [th ? 'คลอรีนอิสระ' : 'Free chlorine', `${f2(poolCl)} ppm`],
            [th ? 'อุณหภูมิน้ำ' : 'Water temp', `${f1(poolT)} °C`],
            [th ? 'ปั๊มกรอง' : 'Filter pump', th ? 'ทำงาน' : 'Running'],
          ] })}
        </div>
        {row(<>
          {Panel({
            title: th ? 'โปรไฟล์ไฟฟ้า & น้ำ วันนี้' : 'Power & water profile · today', note: th ? 'รายชั่วโมง' : 'hourly', grow: '3 1 480px',
            children: <div style={{ height: 240 }}><ApexChartWrapper options={opts} series={series} type="line" height="100%" /></div>,
          })}
          {Panel({
            title: th ? 'ระดับถัง' : 'Tank levels', minW: 260,
            children: (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-around', alignItems: 'flex-end', flex: 1, paddingTop: 6 }}>
                {tank(th ? 'ถังน้ำดิบ' : 'Raw water', rawTank, S[6], '120 m³')}
                {tank(th ? 'ถังบนหลังคา' : 'Roof tank', roofTank, S[0], '30 m³')}
                {tank('LPG', lpgPct, S[1], '2,000 kg')}
                {tank(th ? 'ดีเซล' : 'Diesel', diesel, S[3], '1,000 L')}
              </div>
            ),
          })}
        </>)}
      </>
    );
  };

  // ── Wastewater & environment ───────────────────────────────────────────────
  const wwtpTab = () => {
    const inflow = Math.max(0.3, wave((todayRow.wwIn / 24) * (hourNow > 7 && hourNow < 22 ? 1.3 : 0.6), 0.25, 3));
    const eqLevel = clamp(wave(58, 8, 2, 10), 5, 98);
    const doVal = wave(2.2, 0.35, 4);
    const blanket = wave(0.55, 0.08, 6);
    const clRes = wave(0.8, 0.15, 5);
    const phOut = wave(7.3, 0.12, 1);
    const reuse = clamp(wave(64, 5, 2, 12), 0, 100);
    const mlss = wave(3150, 120, 3);

    const stage = (o: { title: string; value: string; sub: string; state: 'ok' | 'warn' | 'bad'; last?: boolean; color: string }) => (
      <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 150px', minWidth: 140 }}>
        <div style={{ ...card(o.color), flex: 1, padding: '9px 11px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: { ok: OK, warn: WARN, bad: BAD }[o.state] }} />
            <span style={{ fontSize: 10.5, fontWeight: 800, color: MUT, textTransform: 'uppercase', letterSpacing: '.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.title}</span>
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: TXT, marginTop: 4, ...tnum }}>{o.value}</div>
          <div style={{ fontSize: 10.5, color: MUT, ...tnum }}>{o.sub}</div>
        </div>
        {!o.last && <span style={{ color: o.color, fontSize: 18, fontWeight: 800, padding: '0 3px' }} aria-hidden>›</span>}
      </div>
    );

    const latest = EFFLUENT.map((p, i) => {
      const v = p.key === 'bod' ? todayRow.bod : p.key === 'ss' ? todayRow.ss : +(p.base + (seeded(today * 13 + i) - 0.5) * p.amp * 2).toFixed(p.limit < 2 ? 2 : 1);
      const ratio = v / p.limit;
      return { ...p, v, ratio, state: (ratio > 1 ? 'bad' : ratio > 0.8 ? 'warn' : 'ok') as 'ok' | 'warn' | 'bad' };
    });
    const phState = phOut < 5 || phOut > 9 ? 'bad' : 'ok';
    const exceed30 = hist.filter((d) => d.bod > 30 || d.ss > 40).length;
    const near30 = hist.filter((d) => d.bod > 24 || d.ss > 32).length;

    const opts: ApexCharts.ApexOptions = {
      ...baseChart('line'),
      colors: [S[1], S[0]],
      stroke: { width: 2.5, curve: 'smooth' },
      markers: { size: 0, hover: { size: 4 } },
      xaxis: { ...baseChart('line').xaxis, categories: hist.map((d) => d.label), tickAmount: 8 },
      yaxis: { min: 0, max: 50, tickAmount: 5, labels: { style: { colors: axisCol, fontSize: '10px' }, formatter: (v: number) => `${Math.round(v)}` } },
      annotations: {
        yaxis: [
          { y: 30, borderColor: S[1], strokeDashArray: 5, label: { text: th ? 'BOD สูงสุด 30' : 'BOD limit 30', position: 'left', textAnchor: 'start', style: { background: S[1], color: '#fff', fontSize: '10px' } } },
          { y: 40, borderColor: S[0], strokeDashArray: 5, label: { text: th ? 'SS สูงสุด 40' : 'SS limit 40', position: 'left', textAnchor: 'start', style: { background: S[0], color: '#fff', fontSize: '10px' } } },
        ],
      },
      tooltip: { ...baseChart('line').tooltip, y: { formatter: (v: number) => `${f1(v)} mg/L` } },
    };
    const series = [
      { name: 'BOD (mg/L)', data: hist.map((d) => d.bod) },
      { name: 'SS (mg/L)', data: hist.map((d) => d.ss) },
    ];

    const env = [
      { label: th ? 'อุณหภูมิภายนอก' : 'Outdoor temp', value: f1(wave(30.4, 0.8, 2, 12)), unit: '°C', accent: S[1] },
      { label: th ? 'ความชื้น' : 'Humidity', value: `${Math.round(wave(76, 4, 5, 12))}`, unit: '%RH', accent: S[6] },
      { label: 'PM2.5', value: `${Math.round(wave(14, 4, 3))}`, unit: 'µg/m³', accent: S[2], state: 'ok' as const },
      { label: th ? 'เสียงรบกวน (ริมเขตที่ดิน)' : 'Noise (boundary)', value: f1(wave(hourNow > 22 || hourNow < 6 ? 46 : 58, 3, 4)), unit: 'dB(A)', accent: S[5], state: 'ok' as const },
      { label: th ? 'CO₂ วันนี้ (ไฟฟ้า)' : 'CO₂ today (power)', value: int(todayRow.kwh * dayFrac * 0.4999), unit: 'kgCO₂e', accent: S[3] },
    ];

    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'stretch' }}>
          {stage({ title: th ? 'น้ำเสียเข้า' : 'Inflow', value: `${f2(inflow)} m³/h`, sub: `${f1(todayRow.wwIn * dayFrac)} m³ ${th ? 'วันนี้' : 'today'}`, state: 'ok', color: S[4] })}
          {stage({ title: th ? 'บ่อพักน้ำ' : 'Equalization', value: `${Math.round(eqLevel)}%`, sub: th ? 'ปั๊มป้อน P1 ทำงาน' : 'Feed pump P1 on', state: eqLevel > 85 ? 'warn' : 'ok', color: S[3] })}
          {stage({ title: th ? 'บ่อเติมอากาศ' : 'Aeration', value: `DO ${f2(doVal)} mg/L`, sub: `MLSS ${int(mlss)} · ${th ? 'โบลเวอร์' : 'blower'} B1`, state: doVal < 1.5 ? 'warn' : 'ok', color: S[0] })}
          {stage({ title: th ? 'บ่อตกตะกอน' : 'Clarifier', value: `${f2(blanket)} m`, sub: th ? 'ชั้นตะกอน · สูบคืนตะกอน' : 'sludge blanket · RAS on', state: blanket > 0.9 ? 'warn' : 'ok', color: S[5] })}
          {stage({ title: th ? 'ฆ่าเชื้อ' : 'Disinfection', value: `Cl ${f2(clRes)} ppm`, sub: `pH ${f2(phOut)}`, state: clRes < 0.5 ? 'warn' : 'ok', color: S[6] })}
          {stage({ title: th ? 'น้ำทิ้ง / นำกลับ' : 'Effluent / reuse', value: `${Math.round(reuse)}%`, sub: th ? 'ใช้รดน้ำต้นไม้' : 'reused for irrigation', state: 'ok', color: S[2], last: true })}
        </div>
        {row(<>
          {Panel({
            title: th ? 'คุณภาพน้ำทิ้งเทียบมาตรฐาน' : 'Effluent vs discharge standard',
            note: th ? 'อาคารประเภท ข (โรงแรม 60–199 ห้อง)' : 'Building Type B (hotel 60–199 rooms)', grow: '2 1 380px',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  {latest.every((p) => p.state !== 'bad') && phState === 'ok'
                    ? chip('ok', th ? 'ผ่านมาตรฐานทุกพารามิเตอร์' : 'All parameters within limit')
                    : chip('bad', th ? 'มีค่าเกินมาตรฐาน' : 'Limit exceeded')}
                  <span style={{ color: MUT, ...tnum }}>
                    {th ? `30 วัน: เกิน ${exceed30} วัน · ใกล้เกณฑ์ ${near30} วัน` : `30 days: ${exceed30} over · ${near30} near limit`}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                  <span style={{ width: 168, color: TXT, fontWeight: 700 }}>pH</span>
                  <div style={{ flex: 1 }}>{bar(((phOut - 5) / 4) * 100, OK)}</div>
                  <span style={{ width: 92, textAlign: 'right', color: TXT, fontWeight: 700, ...tnum }}>{f2(phOut)}</span>
                  <span style={{ width: 70, textAlign: 'right', color: MUT, ...tnum }}>5 – 9</span>
                </div>
                {latest.map((p) => {
                  const c = { ok: OK, warn: WARN, bad: BAD }[p.state];
                  return (
                    <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                      <span style={{ width: 168, color: TXT, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={th ? p.th : p.en}>
                        {th ? p.th : p.en}
                        <span style={{ color: MUT, fontWeight: 500, fontSize: 10 }}> · {p.src === 'online' ? (th ? 'ออนไลน์' : 'online') : (th ? 'แล็บ' : 'lab')}</span>
                      </span>
                      <div style={{ flex: 1 }}>{bar(p.ratio * 100, c)}</div>
                      <span style={{ width: 92, textAlign: 'right', color: c, fontWeight: 800, ...tnum }}>{p.limit < 2 ? f2(p.v) : f1(p.v)} <span style={{ color: MUT, fontWeight: 500, fontSize: 10 }}>{p.unit}</span></span>
                      <span style={{ width: 70, textAlign: 'right', color: MUT, ...tnum }}>≤ {p.limit}</span>
                    </div>
                  );
                })}
                <div style={{ fontSize: 10, color: MUT, marginTop: 2 }}>
                  {th ? 'อ้างอิงประกาศกระทรวงทรัพยากรธรรมชาติฯ พ.ศ. 2548 — ตรวจสอบประเภทอาคารและค่ามาตรฐานกับใบอนุญาตของไซต์ก่อนใช้งานจริง' : 'Per the 2005 MoNRE building-discharge notification — confirm building class and limits against the site permit.'}
                </div>
              </div>
            ),
          })}
          {Panel({
            title: th ? 'แนวโน้ม BOD / SS น้ำทิ้ง' : 'Effluent BOD / SS trend', note: th ? '30 วัน · mg/L' : '30 days · mg/L', grow: '2 1 380px',
            children: <div style={{ height: 250 }}><ApexChartWrapper options={opts} series={series} type="line" height="100%" /></div>,
          })}
        </>)}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {env.map((e) => <div key={e.label}>{Kpi(e)}</div>)}
        </div>
      </>
    );
  };

  const tabBtn = (id: Tab, label: string) => (
    <button key={id} onClick={() => setTab(id)}
      style={{
        padding: '6px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', borderRadius: 8, border: 'none',
        background: tab === id ? `color-mix(in srgb, ${S[0]} 14%, transparent)` : 'transparent',
        color: tab === id ? S[0] : MUT,
      }}>
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', height: '100%', minHeight: 0, overflow: 'auto', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: TXT }}>{shown.length === 1 ? (th ? LABEL[shown[0]][0] : LABEL[shown[0]][1]) : th ? 'การดำเนินงานรีสอร์ต' : 'Resort Operations'}</span>
        <span title={th ? 'ข้อมูลจำลองเพื่อสาธิต — ยังไม่ได้เชื่อมกับ PMS / มิเตอร์น้ำ / เซนเซอร์บำบัดน้ำเสีย' : 'Simulated demo data — not yet connected to PMS, water meters or WWTP sensors'}
          style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: WARN, padding: '2px 8px', borderRadius: 6, border: `1px solid color-mix(in srgb, ${WARN} 45%, transparent)`, background: `color-mix(in srgb, ${WARN} 10%, transparent)` }}>
          DEMO
        </span>
        <div style={{ display: 'flex', gap: 3, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {shown.length > 1 && shown.map((x) => tabBtn(x, th ? LABEL[x][0] : LABEL[x][1]))}
        </div>
      </div>
      {tab === 'guests' && guestsTab()}
      {tab === 'cost' && costTab()}
      {tab === 'utility' && utilityTab()}
      {tab === 'wwtp' && wwtpTab()}
    </div>
  );
}
